/**
 * Finding a CSV in a reply, and reading it.
 *
 * A skill that produces a schedule answers with a link to it. Rendered as a bare
 * URL that is a dead end — the user has to leave the app, open a spreadsheet, and
 * come back to say what they saw. `bimgo-ai-web` scans the reply text for CSV
 * links and draws each one as a table (`CsvPreview`), which is what makes the
 * answer readable where it was given.
 *
 * **Parsed here rather than with a library.** The reference uses papaparse, whose
 * header mode, streaming and type inference it does not touch: it asks for
 * `header: false, skipEmptyLines: true` and trims each value, which is the whole
 * of what `parseCsv` does. Adding a dependency to a shipped Electron bundle for
 * that is a poor trade when the format's hard parts are few and testable — and
 * they *are* the hard parts, so they are all exercised in `csv.test.mjs`: a
 * quoted comma, a quoted newline, an escaped quote, and CRLF.
 *
 * The thing not ported is GUID click-to-highlight. It looks like part of this
 * feature and is not: v2 wires `onGuidClick` only from `BimChatSidebar`, its
 * IFC-viewer sidebar, and the Revit chat (`ChatContainer`) passes nothing — so
 * GUID cells are plain text there too. 2.5 has no such viewer.
 */

/**
 * Every CSV link in a reply, in the order they appear and without duplicates.
 *
 * Two shapes count, both from the reference: a URL ending in `.csv`, and one
 * whose query marks it as CSV (`?format=csv`, `&type=csv`). A trailing `)` or
 * `.` is stripped — a link at the end of a sentence, or inside markdown
 * parentheses, otherwise carries the punctuation into the request and 404s.
 *
 * The reference also sweeps any line containing the word "csv" for URLs, which
 * matches a link that merely sits in a sentence mentioning CSV. That is dropped
 * deliberately: it turns a passing reference to a file into an attempt to fetch
 * and render something that may not be one, and the two rules above already
 * catch every link a skill actually emits.
 */
export function detectCsvLinks(text: string): string[] {
  if (!text) return [];
  const pattern = /https?:\/\/[^\s<>"'`]+/gi;
  const found: string[] = [];
  const seen = new Set<string>();
  for (const raw of text.match(pattern) ?? []) {
    // Markdown puts links in `(...)` and prose ends them with punctuation;
    // neither is part of the URL.
    const url = raw.replace(/[).,;!\]]+$/, "");
    if (!isLikelyCsvUrl(url) || seen.has(url)) continue;
    seen.add(url);
    found.push(url);
  }
  return found;
}

/** Whether a URL names a CSV — the extension, or a query that says so. */
export function isLikelyCsvUrl(url: string): boolean {
  const lower = url.toLowerCase();
  const path = lower.split("?")[0] ?? "";
  if (path.endsWith(".csv")) return true;
  // `?format=csv`, `&type=csv`, `&download=report.csv` — the query decides when
  // the path does not. Matched on the query alone so a bucket named
  // ".../csv-exports/plan.pdf" is not swept in by its folder.
  const query = lower.slice(lower.indexOf("?") + 1);
  if (lower.includes("?") && /(^|[&=/])csv([&=.]|$)/.test(query)) return true;
  return false;
}

/**
 * Whether the first row names the columns, or is just the first record.
 *
 * The reference renders row 0 as the table head unconditionally — its own
 * comment says "show for first row if it looks like headers" and then never
 * looks. A headerless export therefore loses a record: it is drawn as the column
 * titles and never appears among the rows. Seen on a real CAD-layer export
 * (2026-09-01), where twelve layers rendered as eleven under a header reading
 * `Case_2.dwg | 271331 | A-WALL-RC | …`.
 *
 * The test is deliberately blunt: **a header does not contain a number**.
 * Column names are words; ids, counts, scales and coordinates are the substance
 * of a data row, and one numeric cell in the first row is enough to say it is
 * data. An empty cell says the same — a column with no name is not a name.
 *
 * It cannot separate a headerless file whose first record happens to be all text
 * from a real header, and nothing can without being told. That case degrades the
 * way the reference already behaves, so the change only ever adds correctness.
 */
export function hasHeaderRow(rows: readonly (readonly string[])[]): boolean {
  const first = rows[0];
  if (!first || first.length === 0) return false;
  // A single row is the data. Calling it a header would render a table with a
  // head and nothing under it.
  if (rows.length < 2) return false;
  return first.every((cell) => cell !== "" && !isNumericCell(cell));
}

/** A cell that is only a number — `271331`, `-2.5`, `1e3`, `1,024` excluded (that is two cells). */
function isNumericCell(cell: string): boolean {
  return /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(cell.trim());
}

/**
 * A CSV as rows of cells.
 *
 * RFC 4180, which is four rules: fields are comma-separated; a field may be
 * wrapped in double quotes; inside quotes a comma, a newline and a `""` (one
 * escaped quote) are all literal; rows end at a newline. Everything else in the
 * format is convention.
 *
 * Values are trimmed and blank rows dropped, matching the reference's
 * `transform` and `skipEmptyLines`. Trimming happens **after** parsing, so a
 * deliberately padded quoted field keeps its spaces only if it needs them —
 * which is the same thing papaparse does with that option, and what stops a
 * ragged export rendering with drifting columns.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;

  const endField = () => {
    row.push(field.trim());
    field = "";
  };
  const endRow = () => {
    endField();
    // A blank line is one empty field, not a row of data.
    if (!(row.length === 1 && row[0] === "")) rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] as string;

    if (quoted) {
      if (ch !== '"') {
        field += ch;
        continue;
      }
      // `""` inside a quoted field is one literal quote; a lone `"` closes it.
      if (text[i + 1] === '"') {
        field += '"';
        i += 1;
        continue;
      }
      quoted = false;
      continue;
    }

    if (ch === '"') {
      quoted = true;
      continue;
    }
    if (ch === ",") {
      endField();
      continue;
    }
    if (ch === "\r") {
      // CRLF is one break, not two. A lone CR is treated as a break too — some
      // exporters still emit them, and the alternative is one giant row.
      if (text[i + 1] === "\n") i += 1;
      endRow();
      continue;
    }
    if (ch === "\n") {
      endRow();
      continue;
    }
    field += ch;
  }

  // Whatever is left when the text runs out is the last row, unless the file
  // ended with a newline and there is nothing after it.
  if (field !== "" || row.length > 0) endRow();
  return rows;
}
